import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, Zap, Brain, RefreshCw, Loader2 } from "lucide-react";
import { useGlobalAnalysis, useGlobalAnalysisQuery } from "@/hooks/useGlobalAnalysis";
import { InsightCard } from "./InsightCard";
import { HealthSystemMap } from "./HealthSystemMap";
import { ClinicalTimeline } from "./ClinicalTimeline";
import { RecommendationPanel } from "./RecommendationPanel";
import { GlobalAnalysisLoader } from "./GlobalAnalysisLoader";
import { HealthScoreModal } from "./HealthScoreModal";
import { ActiveAlertsModal } from "./ActiveAlertsModal";
import { motion } from "framer-motion";
import { DataStaleBanner } from "./DataStaleBanner";
import { useStaleFlags, useDataStale } from "@/contexts/DataStaleContext";

interface GlobalAnalysisModernProps {
  patientId: string;
}

export function GlobalAnalysisModern({ patientId }: GlobalAnalysisModernProps) {
  const query = useGlobalAnalysisQuery(patientId);
  const regenerateMutation = useGlobalAnalysis();
  const { clearGlobalAnalysisStale } = useDataStale();
  useStaleFlags(patientId); // Carregar flags do banco

  const [loaderState, setLoaderState] = useState({
    isVisible: false,
    currentStep: 0,
    totalSteps: 4,
    message: ""
  });
  const [showHealthScoreModal, setShowHealthScoreModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);

  const handleGenerate = () => {
    console.log("🎬 Iniciando geração de análise global");

    setLoaderState({
      isVisible: true,
      currentStep: 1,
      totalSteps: 4,
      message: "Carregando dados do paciente..."
    });

    const timeouts: NodeJS.Timeout[] = [];

    timeouts.push(setTimeout(() => {
      console.log("⏱️ Progresso - Etapa 2");
      setLoaderState(prev => ({
        ...prev,
        currentStep: 2,
        message: "Analisando registros de saúde..."
      }));
    }, 1000));

    timeouts.push(setTimeout(() => {
      console.log("⏱️ Progresso - Etapa 3");
      setLoaderState(prev => ({
        ...prev,
        currentStep: 3,
        message: "Gerando insights clínicos..."
      }));
    }, 2500));

    timeouts.push(setTimeout(() => {
      console.log("⏱️ Progresso - Etapa 4");
      setLoaderState(prev => ({
        ...prev,
        currentStep: 4,
        message: "Finalizando análise..."
      }));
    }, 4000));

    regenerateMutation.mutate({ patientId }, {
      onSuccess: () => {
        console.log("✅ Análise concluída - aguardando timeout para fechar loader");
        setTimeout(() => {
          console.log("🚪 Ocultando loader");
          setLoaderState(prev => ({ ...prev, isVisible: false }));
          timeouts.forEach(t => clearTimeout(t));
        }, 500);
      },
      onError: () => {
        console.error("❌ Erro na análise - ocultando loader");
        setLoaderState(prev => ({ ...prev, isVisible: false }));
        timeouts.forEach(t => clearTimeout(t));
      }
    });
  };

  const isLoading = query.isLoading || regenerateMutation.isPending;
  const data = regenerateMutation.data || query.data;

  const handleRegenerate = () => {
    console.log("🔄 Regenerando análise global");

    setLoaderState({
      isVisible: true,
      currentStep: 1,
      totalSteps: 4,
      message: "Carregando dados do paciente..."
    });

    const timeouts: NodeJS.Timeout[] = [];

    timeouts.push(setTimeout(() => {
      setLoaderState(prev => ({
        ...prev,
        currentStep: 2,
        message: "Analisando registros de saúde..."
      }));
    }, 1000));

    timeouts.push(setTimeout(() => {
      setLoaderState(prev => ({
        ...prev,
        currentStep: 3,
        message: "Gerando insights clínicos..."
      }));
    }, 2500));

    timeouts.push(setTimeout(() => {
      setLoaderState(prev => ({
        ...prev,
        currentStep: 4,
        message: "Finalizando análise..."
      }));
    }, 4000));

    regenerateMutation.mutate({ patientId }, {
      onSuccess: async () => {
        // Limpar apenas o flag da análise global após regeneração bem-sucedida
        try {
          await clearGlobalAnalysisStale(patientId);
        } catch (error) {
          console.error("Error clearing stale flag:", error);
        }
        setTimeout(() => {
          setLoaderState(prev => ({ ...prev, isVisible: false }));
          timeouts.forEach(t => clearTimeout(t));
        }, 500);
      },
      onError: () => {
        setLoaderState(prev => ({ ...prev, isVisible: false }));
        timeouts.forEach(t => clearTimeout(t));
      }
    });
  };

  // Se não há dados, mostrar tela de boas-vindas
  if (!data && !isLoading) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow-lg border-2 border-dashed">
            <CardContent className="py-16 px-8 text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center mb-6">
                <Brain className="h-8 w-8 text-white" />
              </div>

              <h3 className="text-2xl font-bold mb-3 bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
                Análise Global Inteligente
              </h3>

              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                Gere uma análise abrangente dos registros de saúde deste paciente com IA avançada.
                Obtenha insights visuais, tendências e recomendações priorizadas.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  onClick={handleGenerate}
                  className="gap-3 bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all text-base"
                >
                  <Sparkles className="h-5 w-5" />
                  <span className="font-semibold">Gerar Análise Global</span>
                </Button>
              </div>
              
              {/* Banner de dados desatualizados */}
              <div className="mt-6">
                <DataStaleBanner
                  patientId={patientId}
                  type="globalAnalysis"
                  onUpdate={handleRegenerate}
                  isUpdating={regenerateMutation.isPending}
                />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <GlobalAnalysisLoader
          isVisible={loaderState.isVisible}
          currentStep={loaderState.currentStep}
          totalSteps={loaderState.totalSteps}
          message={loaderState.message}
        />
      </>
    );
  }

  // Skeleton de loading
  if (isLoading) {
    return (
      <>
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-40" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
          <Skeleton className="h-48" />
          <Skeleton className="h-96" />
        </div>

        <GlobalAnalysisLoader
          isVisible={loaderState.isVisible}
          currentStep={loaderState.currentStep}
          totalSteps={loaderState.totalSteps}
          message={loaderState.message}
        />
      </>
    );
  }

  // Dados mockados para demonstração (substituir com dados reais da API)
  const insights = [
    {
      title: "Score de Saúde",
      value: data?.health_score || 0,
      unit: "/100",
      trend: "stable" as const,
      status: (data?.health_score || 0) >= 80 ? "good" as const
        : (data?.health_score || 0) >= 60 ? "normal" as const
          : "warning" as const,
      description: "Baseado em registros de saúde",
      onClick: () => setShowHealthScoreModal(true)
    },
    {
      title: "Alertas Ativos",
      value: data?.active_alerts?.length || 0,
      status: "warning" as const,
      description: "Requerem atenção imediata",
      onClick: () => setShowAlertsModal(true)
    },
    {
      title: "Registros Analisados",
      value: data?.stats?.records || 0,
      status: "normal" as const,
      description: "Total de registros no sistema"
    }
  ];

  const systems = data?.systems || [];
  const timeline = data?.clinical_timeline || [];
  const recommendations = data?.recommendations || [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        {/* Banner de dados desatualizados */}
        <DataStaleBanner
          patientId={patientId}
          type="globalAnalysis"
          onUpdate={handleRegenerate}
          isUpdating={regenerateMutation.isPending}
        />

        {/* Header com botão de regenerar */}
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Análise Global Inteligente</h2>
          <Button
            onClick={handleRegenerate}
            disabled={regenerateMutation.isPending}
            variant="outline"
            size="sm"
          >
            {regenerateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Gerando...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Regenerar análise
              </>
            )}
          </Button>
        </div>

        {/* Cards de Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {insights.map((insight, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <InsightCard {...insight} index={i} onClick={insight.onClick} />
            </motion.div>
          ))}
        </div>

        {/* Resumo Clínico */}
        {data?.summary && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5 text-primary" />
                Resumo Clínico
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                {data.summary}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Mapa de Sistemas */}
        {systems.length > 0 ? (
          <HealthSystemMap systems={systems} />
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p className="text-sm">Mapa de sistemas será gerado na próxima análise</p>
            </CardContent>
          </Card>
        )}

        {/* Linha do Tempo Clínica */}
        {timeline.length > 0 && <ClinicalTimeline events={timeline} />}

        {/* Painel de Recomendações */}
        {recommendations.length > 0 ? (
          <RecommendationPanel recommendations={recommendations} />
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p className="text-sm">Recomendações serão geradas na próxima análise</p>
            </CardContent>
          </Card>
        )}

        {/* Highlights e Seções Detalhadas */}
        {data?.highlights && data.highlights.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-primary" />
                Destaques Clínicos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.highlights.map((highlight: any, i: number) => (
                <div
                  key={i}
                  className={`p-4 rounded-lg border-l-4 ${highlight.type === "warning"
                    ? "border-orange-500 bg-orange-50 dark:bg-orange-950/20"
                    : highlight.type === "success"
                      ? "border-green-500 bg-green-50 dark:bg-green-950/20"
                      : "border-blue-500 bg-blue-50 dark:bg-blue-950/20"
                    }`}
                >
                  <h4 className="font-semibold mb-1">{highlight.title}</h4>
                  <p className="text-sm text-muted-foreground">{highlight.message}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {data?.sections && data.sections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Análise Detalhada por Sistemas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {data.sections.map((section: any, i: number) => (
                <div key={i} className="space-y-3">
                  <h3 className="font-semibold text-lg text-primary">{section.title}</h3>
                  <ul className="space-y-2 list-disc list-inside text-sm text-muted-foreground">
                    {section.bullets?.map((bullet: string, j: number) => (
                      <li key={j} className="leading-relaxed">{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </motion.div>

      <HealthScoreModal
        open={showHealthScoreModal}
        onClose={() => setShowHealthScoreModal(false)}
        score={data?.health_score || 0}
        breakdown={data?.health_score_breakdown || {
          score: 0,
          components: { lab_score: 0, lab_weight: 50, completeness_score: 0, completeness_weight: 30, vital_score: 0, vital_weight: 20 },
          metrics: { total_lab_results: 0, abnormal_results: 0, abnormal_percentage: 0, vital_signs_count: 0, completeness_percentage: 0 }
        }}
      />

      <ActiveAlertsModal
        open={showAlertsModal}
        onClose={() => setShowAlertsModal(false)}
        alerts={data?.active_alerts || []}
      />

      <GlobalAnalysisLoader
        isVisible={loaderState.isVisible}
        currentStep={loaderState.currentStep}
        totalSteps={loaderState.totalSteps}
        message={loaderState.message}
      />
    </>
  );
}
